// ============================================================================
// lib/mail/mailData.ts — данные экрана «Почта: Входящие».
//
// Список ЯЩИКОВ — MailService (SDK `sdk.mail.listMailAccounts`): доступ,
// изоляцию по владельцу и лимиты считает сервер, секретов в ответе нет.
// Письма, папки догона и вложения серверное почтовое ядро (internal/mail)
// держит в таблицах mail_messages / mail_sync_state / mail_attachments —
// экран читает их штатным RPC MetadataService.ExecuteHydraql (тот же путь,
// что у отчётов и произвольных списков); права проверяет сервер, клиент не
// работает с БД напрямую.
//
// Здесь — ТОЛЬКО доступ к данным и их нормализация (SRP): UI не знает про
// HydraQL, имена колонок и форматы значений сервера.
// ============================================================================

import type { MailAccount as ProtoMailAccount } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';

/** Код приложения «Почта» (kind='app', appKind='mail'). */
export const MAIL_APP_CODE = 'mail';

/**
 * Сколько последних писем ящика держим для списка и поиска.
 * Серверный HydraQL отдаёт строку целиком (включая тело письма), поэтому окно
 * ограничено: интерфейс честно подписывает, что считает и ищет по этим письмам.
 */
export const MAIL_WINDOW_LIMIT = 200;

export interface MailAccount {
  id: string;
  name: string;
  email: string;
  displayName: string;
  enabled: boolean;
  lastSyncAt: string;
}

export interface MailFolder {
  accountId: string;
  folder: string;
  lastUid: string;
  lastSyncAt: string;
}

export interface MailMessage {
  id: string;
  accountId: string;
  threadId: string;
  subject: string;
  fromName: string;
  fromAddress: string;
  createdAt: string;
  receivedAt: string;
  isRead: boolean;
  isDeleted: boolean;
  folder: string;
  size: number;
  textBody: string;
}

export interface MailMessageDetail {
  textBody: string;
  htmlBody: string;
}

export interface MailAttachmentMeta {
  filename: string;
  contentType: string;
  size: number;
  inline: boolean;
}

export interface MailFolderView {
  /** Ключ папки: '' — «Все письма», иначе имя папки на сервере. */
  key: string;
  label: string;
  total: number;
  unread: number;
}

const FOLDER_QUERY =
  'SELECT folder, account_id, last_uid, last_sync_at FROM mail_sync_state WHERE account_id = :account ORDER BY folder';

const MESSAGE_COLUMNS =
  'id, account_id, thread_id, subject, from_name, from_address, created_at, received_at, is_read, is_deleted, folder, size, text_body';

const MESSAGE_QUERY = `SELECT ${MESSAGE_COLUMNS} FROM mail_messages WHERE account_id = :account ORDER BY created_at DESC LIMIT ${MAIL_WINDOW_LIMIT}`;

const MESSAGE_DETAIL_QUERY =
  'SELECT text_body, html_body FROM mail_messages WHERE id = :id LIMIT 1';

const ATTACHMENT_QUERY =
  'SELECT filename, content_type, size, is_inline FROM mail_attachments WHERE message_id = :id ORDER BY filename';

/** Ответ HydraQL → плоские строки (значения приходят строками). */
export async function queryRows(
  query: string,
  params: Record<string, string> = {},
): Promise<Record<string, string>[]> {
  const response = (await getSDK().metadata.executeHydraql({ query, params })) as unknown as {
    rows?: Array<{ values?: Array<{ column?: string; value?: unknown }> }>;
  };
  return (response.rows || []).map((row) => {
    const out: Record<string, string> = {};
    for (const cell of row.values || []) {
      const column = String(cell.column || '');
      if (!column) continue;
      out[column] = normalizeCell(cell.value);
    }
    return out;
  });
}

/** Значение HydraQL → строка (NULL и «<nil>» — пусто; остальное как есть). */
function normalizeCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (text === '<nil>' || text === 'null') return '';
  return text;
}

function toBool(value: string): boolean {
  const text = value.trim().toLowerCase();
  return text === 'true' || text === 't' || text === '1' || text === 'yes' || text === 'да';
}

function toInt(value: string): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function mapAccount(account: ProtoMailAccount): MailAccount {
  const email = String(account.email || '');
  const displayName = String(account.displayName || '');
  return {
    id: String(account.id || ''),
    name: String(account.name || '') || displayName || email,
    email,
    displayName,
    enabled: !!account.enabled,
    lastSyncAt: String(account.lastSyncAt || ''),
  };
}

function mapFolder(row: Record<string, string>): MailFolder {
  return {
    accountId: row.account_id || '',
    folder: row.folder || '',
    lastUid: row.last_uid || '',
    lastSyncAt: row.last_sync_at || '',
  };
}

function mapMessage(row: Record<string, string>): MailMessage {
  return {
    id: row.id || '',
    accountId: row.account_id || '',
    threadId: row.thread_id || '',
    subject: row.subject || '',
    fromName: row.from_name || '',
    fromAddress: row.from_address || '',
    createdAt: row.created_at || row.received_at || '',
    receivedAt: row.received_at || '',
    isRead: toBool(row.is_read),
    isDeleted: toBool(row.is_deleted),
    folder: row.folder || '',
    size: toInt(row.size),
    textBody: row.text_body || '',
  };
}

/** Включённые ящики текущего пользователя (порядок — по имени на сервере). */
export async function loadMailAccounts(): Promise<MailAccount[]> {
  const response = await getSDK().mail.listMailAccounts({ includeDisabled: false });
  const items = (response.items || []) as ProtoMailAccount[];
  return items.map(mapAccount).filter((account) => !!account.id);
}

/** Папки ящика, известные догону (mail_sync_state). */
export async function loadMailFolders(accountId: string): Promise<MailFolder[]> {
  if (!accountId) return [];
  const rows = await queryRows(FOLDER_QUERY, { account: accountId });
  return rows.map(mapFolder).filter((folder) => !!folder.folder);
}

/** Последние письма ящика (все папки; без удалённых). */
export async function loadMailMessages(accountId: string): Promise<MailMessage[]> {
  if (!accountId) return [];
  const rows = await queryRows(MESSAGE_QUERY, { account: accountId });
  return rows
    .map(mapMessage)
    .filter((message) => !!message.id && !message.isDeleted);
}

/** Тело выбранного письма (text/html). */
export async function loadMailMessageDetail(messageId: string): Promise<MailMessageDetail> {
  if (!messageId) return { textBody: '', htmlBody: '' };
  const rows = await queryRows(MESSAGE_DETAIL_QUERY, { id: messageId });
  const row = rows[0] || {};
  return { textBody: row.text_body || '', htmlBody: row.html_body || '' };
}

/** Метаданные вложений письма (без содержимого). */
export async function loadMailAttachments(messageId: string): Promise<MailAttachmentMeta[]> {
  if (!messageId) return [];
  const rows = await queryRows(ATTACHMENT_QUERY, { id: messageId });
  return rows.map((row) => ({
    filename: row.filename || 'файл',
    contentType: row.content_type || '',
    size: toInt(row.size),
    inline: toBool(row.is_inline),
  }));
}

/** Человеческое имя папки (сервер хранит имена IMAP). */
export function folderLabel(folder: string): string {
  const key = folder.trim().toUpperCase();
  switch (key) {
    case 'INBOX': return 'Входящие';
    case 'SENT':
    case 'SENT ITEMS':
    case 'SENT MESSAGES': return 'Отправленные';
    case 'DRAFTS': return 'Черновики';
    case 'TRASH':
    case 'DELETED':
    case 'DELETED ITEMS': return 'Удалённые';
    case 'JUNK':
    case 'SPAM': return 'Спам';
    case 'ARCHIVE': return 'Архив';
    default: return folder || 'Без папки';
  }
}

/** Представление имени отправителя. */
export function senderLabel(message: MailMessage): string {
  return message.fromName || message.fromAddress || 'Неизвестный отправитель';
}

/** Короткий фрагмент письма (одна строка, без разметки). */
export function messageSnippet(message: MailMessage, limit = 160): string {
  const text = plainText(message.textBody).replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

/** HTML → безопасный текст (DOMParser не исполняет скрипты). */
export function plainText(html: string): string {
  if (!html) return '';
  if (!/[<>&]/.test(html)) return html;
  try {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.body?.textContent || '';
  } catch {
    return html.replace(/<[^>]*>/g, ' ');
  }
}

/**
 * Список папок для боковой колонки: «Все письма» + INBOX + папки догона +
 * папки, встречающиеся в письмах. Счётчики — по загруженному окну писем
 * (в интерфейсе это подписано, чтобы не выдавать часть за целое).
 */
export function buildFolderViews(messages: MailMessage[], folders: MailFolder[]): MailFolderView[] {
  const names = new Set<string>(['INBOX']);
  for (const folder of folders) if (folder.folder) names.add(folder.folder);
  for (const message of messages) if (message.folder) names.add(message.folder);

  const counts = new Map<string, { total: number; unread: number }>();
  for (const message of messages) {
    const key = message.folder || '';
    const current = counts.get(key) || { total: 0, unread: 0 };
    current.total += 1;
    if (!message.isRead) current.unread += 1;
    counts.set(key, current);
  }

  const ordered = [...names].sort((a, b) => {
    if (a.toUpperCase() === 'INBOX') return -1;
    if (b.toUpperCase() === 'INBOX') return 1;
    return folderLabel(a).localeCompare(folderLabel(b), 'ru');
  });

  const totalAll = messages.length;
  const unreadAll = messages.filter((message) => !message.isRead).length;
  const views: MailFolderView[] = [
    { key: '', label: 'Все письма', total: totalAll, unread: unreadAll },
  ];
  for (const name of ordered) {
    const count = counts.get(name) || { total: 0, unread: 0 };
    views.push({ key: name, label: folderLabel(name), total: count.total, unread: count.unread });
  }
  return views;
}

/** Фильтр писем: папка ('' = все) и поиск по теме/отправителю/тексту. */
export function filterMessages(
  messages: MailMessage[],
  folderKey: string,
  query: string,
): MailMessage[] {
  const needle = query.trim().toLowerCase();
  return messages.filter((message) => {
    if (folderKey && message.folder !== folderKey) return false;
    if (!needle) return true;
    return [message.subject, message.fromName, message.fromAddress, message.textBody]
      .some((value) => value.toLowerCase().includes(needle));
  });
}
